import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../utils/axiosConfig";

import { Container, Paper, Typography, Box, Button } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

import { handleApiError } from "../../utils/apiErrorHandler";

interface Competition {
  _id: string;
  name: string;
  location: string;
  startDate: string;
  endDate: string;
  registrationDeadline: string;
  registrationOpenDate: string;
}

const CompetitionsListPage: React.FC = () => {
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // 🔹 Wettkämpfe abrufen
  const fetchCompetitions = async () => {
    try {
      const response = await axios.get("/api/competitions");
      setCompetitions(response.data);
    } catch (error) {
      handleApiError(error, "Fehler beim Laden der Wettkämpfe.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompetitions();
  }, []);

  // 🔹 Wettkampf löschen
  const deleteCompetition = async (id: string) => {
    if (!window.confirm("Wettkampf wirklich löschen?")) return;

    try {
      await axios.delete(`/api/competitions/${id}`);
      setCompetitions(competitions.filter((c) => c._id !== id));
    } catch (error) {
      handleApiError(error, "Fehler beim Löschen des Wettkampfs.");
    }
  };

  // 🔹 Datum formatieren
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  // 🔹 Spalten für das DataGrid definieren
  const columns: GridColDef[] = [
    { field: "name", headerName: "Name", flex: 1, minWidth: 200 },
    { field: "location", headerName: "Ort", flex: 1, minWidth: 150 },
    {
      field: "startDate",
      headerName: "Startdatum",
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => formatDate((params.row as Competition).startDate),
    },
    {
      field: "endDate",
      headerName: "Enddatum",
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => formatDate((params.row as Competition).endDate),
    },
    {
      field: "registrationOpenDate",
      headerName: "Meldestart",
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => formatDate((params.row as Competition).registrationOpenDate),
    },
    {
      field: "registrationDeadline",
      headerName: "Enddatum",
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => formatDate((params.row as Competition).registrationDeadline),
    },
    {
      field: "actions",
      headerName: "Aktionen",
      flex: 1,
      minWidth: 200,
      sortable: false,
      renderCell: (params) => {
        const competition = params.row as Competition;
        return (
          <Box>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/admin/competitions/edit/${competition._id}`)}
              sx={{ mr: 1 }}
            >
              Bearbeiten
            </Button>
            <Button
              variant="contained"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={() => deleteCompetition(competition._id)}
            >
              Löschen
            </Button>
          </Box>
        );
      },
    },
  ];

  return (
    <Container component="main" maxWidth="xl">
      <Paper elevation={3} sx={{ padding: 3, mt: 2 }}>
        <Typography variant="h5" gutterBottom>
          Wettkampfverwaltung
        </Typography>

        {/* 🔹 Button für neuen Wettkampf */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate("/admin/competitions/new")}
          >
            Neuer Wettkampf
          </Button>
        </Box>

        {/* 🔹 DataGrid für Wettkämpfe */}
        <Box sx={{ height: 500, width: "100%", mt: 2 }}>
          <DataGrid
            rows={competitions}
            columns={columns}
            getRowId={(row) => row._id}
            pageSizeOptions={[10, 25, 50]}
            loading={loading}
          />
        </Box>
      </Paper>
    </Container>
  );
};

export default CompetitionsListPage;
